import * as React from 'react';
import { useState } from 'react';
import { Filters, Summary, AuditGrid, ShipmentsGrid } from './components';
import { withStyles } from '@material-ui/core';
import {
  RouteAuditData,
  RouteAuditRecord,
  RouteAuditRequest,
  RouteAuditSummary,
  Shipment
} from './IRouteAudit';
import { getDataWithShipmentIssues, getShipmentsList, getSummaryData } from './utils';
import { postData } from '../../../../api/componentAPIs';
import MESSAGES from '../../../../appMessages';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import { formatToISODate } from '../../../common/utils/dateTimeUtils';
import { withUserProfile } from '../../../../userProfile/userProfileContext';
const styles = () => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '0 20px'
  },
  gridSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    paddingTop: 10,
    paddingBottom: 10,
    gap: '10px'
  }
});

const defaultSummaryData: RouteAuditSummary = {
  totalAudits: 0,
  totalShipmentsWithIssues: 0,
  totalShortageShipments: 0,
  totalDamagedShipments: 0,
  totalOutOfSequenceShipments: 0,
  totalRecouped: 0,
  averageQualityRating: 0
};

interface RouteAuditReportProps {
  classes: any;
  enqueueSnackbar: (message: string, options: any) => void;
  userData: any;
}

interface GridVisibility {
  audit: boolean;
  shipments: boolean;
}

const gridTypes = {
  audit: 'audit',
  shipments: 'shipments'
};

const RouteAuditReport = ({ classes, enqueueSnackbar, userData }: RouteAuditReportProps) => {
  const [summaryData, setSummaryData] = useState<RouteAuditSummary>(defaultSummaryData);
  const [routeAuditData, setRouteAuditData] = useState<RouteAuditData | null>(null);
  const [allShipmentsData, setAllShipmentsData] = useState<Shipment[]>([]);
  const [selectedShipmentsData, setSelectedShipmentsData] = useState<Shipment[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gridVisibility, setGridVisibility] = useState<GridVisibility>({
    audit: true,
    shipments: true
  });


  // Function to get route audit data
  const getRouteAuditData = async (request: RouteAuditRequest) => {
    setIsLoading(true);
    const payload = {
      startDate: formatToISODate(request.startDate),
      endDate: formatToISODate(request.endDate),
      terminalCodes: request.terminalCodes
    };
    try {
      const response: any = await postData('GetRouteAudit', payload);
      if (response?.status === 200) {
        const data = response.data as RouteAuditData;
        populateData(data);
      } else {
        populateData([]);
        handleError(response);
      }
    } catch (error) {
      populateData([]);
      handleError(error);
    }
    setIsLoading(false);
  };

  // Function to handle error in case of API call failure
  const handleError = (error: any) => {
    const errorMessage =
      error.response?.data?.message || MESSAGES.TERMINAL_ACCESS.FETCH_TERMINAL_DATA_FAILURE;
    enqueueSnackbar(errorMessage, { variant: 'error' });
  };

  // Function to populate the data in the state
  const populateData = (data: RouteAuditData) => {
    const auditData = getDataWithShipmentIssues(data);
    setRouteAuditData(auditData);

    const allShipmentsData = getShipmentsList(auditData);
    setAllShipmentsData(allShipmentsData);
    setSelectedShipmentsData(allShipmentsData);

    const summaryData = getSummaryData(auditData);
    setSummaryData(summaryData);
  };

  const onAuditGridRowsSelected = (records: RouteAuditRecord[]) => {
    if (records.length > 0) {
      const selectedShipmentsData = getShipmentsList(records);
      setSelectedShipmentsData(selectedShipmentsData);
    } else {
      setSelectedShipmentsData(allShipmentsData);
    }
  };

  const handleGridVisibility = (gridType: string) => (isMaximized: boolean) => {
    setGridVisibility(() => {
      if (isMaximized) {
        // Show only the selected grid when maximized
        return {
          audit: gridType === gridTypes.audit,
          shipments: gridType === gridTypes.shipments
        };
      }

      // Show both grids when not maximized
      return { audit: true, shipments: true };
    });
  };

  return (
    <div className={classes.container}>
      <Filters getRouteAuditData={getRouteAuditData} isLoading={isLoading} />
      <Summary data={summaryData} />
      <div className={classes.gridSection}>
        <AuditGrid
          data={routeAuditData}
          onRowsSelected={onAuditGridRowsSelected}
          onToggleGridMaximize={handleGridVisibility(gridTypes.audit)}
          visible={gridVisibility.audit}
          userData={userData}
        />
        <ShipmentsGrid
          data={selectedShipmentsData}
          onToggleGridMaximize={handleGridVisibility(gridTypes.shipments)}
          visible={gridVisibility.shipments}
          userData={userData}
        />
      </div>
    </div>
  );
};

export default compose(withStyles(styles), withSnackbar, withUserProfile)(RouteAuditReport as any);
